'use client'

import { useState } from 'react'
import { X, Loader2, Sparkles } from 'lucide-react'
import { createClient, isSupabaseConfigured } from '@/lib/supabase/client'
import { Z } from '@/lib/z-index'
import { useFreemium } from '@/lib/freemium-context'
import { signInWithGooglePopup } from '@/lib/oauth-popup'
import { toast } from 'sonner'

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
)

interface SignupGateModalProps {
  open: boolean
  onClose: () => void
  /** Ouvre le formulaire d'inscription (modale in-page). */
  onRegister: () => void
  /** Ouvre le formulaire de connexion (modale in-page). */
  onLogin: () => void
  /** Rafraîchit le profil de la page (header/crédits) après connexion. */
  onAuthSuccess?: () => void
}

/**
 * Popup affichée quand un visiteur non connecté tente de lancer un traitement.
 * La navigation et l'upload restent libres — seul le lancement déclenche cette modale.
 * Les fichiers déjà uploadés sont conservés : l'authentification se fait en modale
 * (in-page), sans recharger la page.
 */
export default function SignupGateModal({ open, onClose, onRegister, onLogin, onAuthSuccess }: SignupGateModalProps) {
  const [googleLoading, setGoogleLoading] = useState(false)
  const { refreshLimits } = useFreemium()

  if (!open) return null

  const handleGoogleSignIn = async () => {
    if (!isSupabaseConfigured) { toast.error('Authentification non configurée.'); return }
    const supabase = createClient()
    if (!supabase) { toast.error('Authentification non configurée.'); return }
    setGoogleLoading(true)
    // Connexion via popup : la page reste vivante → le fichier uploadé et la
    // reprise automatique du traitement sont préservés. Repli sur redirection
    // si le popup est bloqué (cf. signInWithGooglePopup).
    await signInWithGooglePopup({
      supabase,
      onAuthenticated: () => {
        // Re-lit l'état d'auth → isAuthenticated bascule → la reprise du
        // traitement en attente se déclenche. Fichier toujours en mémoire.
        void refreshLimits()
        onAuthSuccess?.()
        setGoogleLoading(false)
        onClose()
      },
      onCancel: () => setGoogleLoading(false),
      onError: (msg) => { toast.error('Erreur Google : ' + msg) },
    })
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: Z.modal, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', width: '100%', maxWidth: 420, position: 'relative' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <button onClick={onClose} aria-label="Fermer" style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <X size={20} color="#9CA3AF" />
        </button>

        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <img src="/qualyse-logo.png" alt="Qualyse" style={{ width: 32, height: 32, borderRadius: 8 }} />
          <span style={{ fontSize: 16, fontWeight: 800, color: '#111827', fontFamily: 'Georgia, serif' }}>Qualyse</span>
        </div>

        {/* Title */}
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 8px', lineHeight: 1.3 }}>
          Créez votre compte gratuit pour continuer
        </h2>
        <p style={{ fontSize: 13.5, color: '#6B7280', margin: '0 0 20px', lineHeight: 1.55 }}>
          Vos fichiers sont prêts. Créez votre compte gratuit et recevez{' '}
          <strong style={{ color: '#1B4332', fontWeight: 700 }}>100 crédits</strong>{' '}
          pour lancer votre première analyse.
        </p>

        {/* Primary — register */}
        <button
          type="button"
          onClick={onRegister}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0', borderRadius: 8, fontSize: 14, fontWeight: 700, color: '#fff', background: '#1B4332', border: 'none', cursor: 'pointer', transition: 'background .15s' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#163d28')}
          onMouseLeave={e => (e.currentTarget.style.background = '#1B4332')}
        >
          <Sparkles size={16} />
          Créer mon compte gratuit
        </button>

        {/* Secondary — login */}
        <button
          type="button"
          onClick={onLogin}
          style={{ width: '100%', marginTop: 10, padding: '11px 0', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#1B4332', background: '#fff', border: '1.5px solid #1B4332', cursor: 'pointer', transition: 'background .15s' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#F0FAF4')}
          onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
        >
          J&apos;ai déjà un compte
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
          <div style={{ flex: 1, height: 1, background: '#F3F4F6' }} />
          <span style={{ fontSize: 12, color: '#6B7280' }}>ou</span>
          <div style={{ flex: 1, height: 1, background: '#F3F4F6' }} />
        </div>

        {/* Google OAuth */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={googleLoading}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '11px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', background: '#fff', fontSize: 14, fontWeight: 600, color: '#374151', cursor: googleLoading ? 'wait' : 'pointer', opacity: googleLoading ? 0.6 : 1 }}
        >
          {googleLoading ? <Loader2 size={16} className="animate-spin" /> : <GoogleIcon />}
          Continuer avec Google
        </button>
      </div>
    </div>
  )
}
